import type { RateGrid } from "@redotech/admin-sdk/rpc/schema/rate-tables/parse-carrier-invoice-csv";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import * as styles from "./comparison-rate-table.module.css";

interface OrderDistributionTableProps {
  rateGrid: RateGrid;
}

export const OrderDistributionTable = memo(function OrderDistributionTable({
  rateGrid,
}: OrderDistributionTableProps) {
  const { distributionData, totalOrders } = useMemo(() => {
    let total = 0;
    const data: {
      weight: number;
      zone: string;
      count: number;
      pct: number;
    }[][] = [];

    // Calculate total orders
    for (const row of rateGrid.countGrid) {
      for (const count of row) {
        total += count;
      }
    }

    // Build percentage grid
    for (let wi = 0; wi < rateGrid.weightBucketsOz.length; wi++) {
      const row: {
        weight: number;
        zone: string;
        count: number;
        pct: number;
      }[] = [];
      for (let zi = 0; zi < rateGrid.zones.length; zi++) {
        const count = rateGrid.countGrid[wi]?.[zi] ?? 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        row.push({
          weight: rateGrid.weightBucketsOz[wi]!,
          zone: rateGrid.zones[zi]!,
          count,
          pct,
        });
      }
      data.push(row);
    }

    return { distributionData: data, totalOrders: total };
  }, [rateGrid]);

  // Calculate min/max for heat mapping
  const { minPct, maxPct } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const row of distributionData) {
      for (const cell of row) {
        if (cell.pct > 0) {
          min = Math.min(min, cell.pct);
          max = Math.max(max, cell.pct);
        }
      }
    }
    return {
      minPct: min === Infinity ? 0 : min,
      maxPct: max === -Infinity ? 0 : max,
    };
  }, [distributionData]);

  const getHeatColor = (pct: number): string => {
    if (pct === 0) return "#f3f4f6"; // gray for empty
    if (maxPct === minPct) return "hsl(210, 55%, 82%)"; // uniform color

    const ratio = Math.max(0, Math.min(1, (pct - minPct) / (maxPct - minPct)));
    const lightness = 92 - ratio * 40;
    return `hsl(210, 55%, ${lightness}%)`;
  };

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" textColor="secondary">
        Total Orders: {totalOrders.toLocaleString()}
      </Text>

      <Flex
        bgColor="secondary"
        borderColor="primary"
        borderWidth="1px"
        className={styles.tableContainer}
        overflow="auto"
        radius="md"
      >
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeaderWeight}>Weight</th>
              {rateGrid.zones.map((zone, i) => (
                <th className={styles.tableHeaderZone} key={i}>
                  Z{zone}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {distributionData.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td className={styles.tableRowHeader}>
                  {rateGrid.weightBucketsOz[rowIdx]}oz
                </td>
                {row.map((cell, colIdx) => (
                  <td
                    className={styles.tableCellDefault}
                    key={colIdx}
                    style={{ backgroundColor: getHeatColor(cell.pct) }}
                    title={`Orders: ${cell.count}\nPercentage: ${cell.pct.toFixed(2)}%`}
                  >
                    {cell.pct > 0 ? `${cell.pct.toFixed(1)}%` : "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Flex>
    </Flex>
  );
});
